import { FunctionalComponent } from "preact";
import { useState, useEffect, useCallback, useRef } from "preact/hooks";
import { Header } from "./components/Header";
import { SearchBar } from "./components/SearchBar";
import { BookmarkList } from "./components/BookmarkList";
import { BookmarkModal } from "./components/BookmarkModal";
import { BookmarkDetail } from "./components/BookmarkDetail";
import { LoginModal } from "./components/LoginModal";
import { ReadingView } from "./components/ReadingView";
import { ImportExportModal } from "./components/ImportExportModal";
import { ToastProvider, useToast } from "./components/Toast";
import { TagFilter } from "./components/TagFilter";
import { api, Bookmark, Tag } from "./utils/api";
import { useKeyboardShortcuts } from "./hooks/useKeyboardShortcuts";

type ViewFilter = "all" | "read-later" | "read";

const AppContent: FunctionalComponent = () => {
  const { showToast } = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [readingBookmarkId, setReadingBookmarkId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ViewFilter>("all");
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [importExportModalOpen, setImportExportModalOpen] = useState(false);
  const [detailBookmark, setDetailBookmark] = useState<Bookmark | null>(null);
  const [editingBookmark, setEditingBookmark] = useState<Bookmark | null>(null);

  const PAGE_SIZE = 50;
  const offsetRef = useRef(0);

  // Check for existing API key on mount
  useEffect(() => {
    const apiKey = localStorage.getItem("pbookmarks_api_key");
    if (apiKey) {
      setIsAuthenticated(true);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchBookmarks = useCallback(async (loadMore = false) => {
    if (!isAuthenticated) return;
    try {
      const currentOffset = loadMore ? offsetRef.current : 0;
      const response = await api.getBookmarks({ 
        search: search || undefined, 
        tag: tagFilter || undefined,
        limit: PAGE_SIZE,
        offset: currentOffset,
      });
      
      if (loadMore) {
        setBookmarks((prev) => [...prev, ...response.data]);
      } else {
        setBookmarks(response.data);
      }
      setTotalCount(response.meta?.total ?? response.data.length);
      const newOffset = currentOffset + response.data.length;
      offsetRef.current = newOffset;
      setOffset(newOffset);
    } catch (err) {
      if ((err as Error).message.includes("Unauthorized")) {
        setIsAuthenticated(false);
        localStorage.removeItem("pbookmarks_api_key");
        showToast("Session expired. Please log in again.", "warning");
      } else {
        showToast(err instanceof Error ? err.message : "Failed to load bookmarks", "error");
      }
    }
  }, [search, tagFilter, isAuthenticated, showToast]);

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || offsetRef.current >= totalCount) return;
    setLoadingMore(true);
    await fetchBookmarks(true);
    setLoadingMore(false);
  }, [fetchBookmarks, loadingMore, totalCount]);

  const fetchTags = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await api.getTags();
      setTags(response.data);
    } catch (err) {
      console.error("Failed to load tags:", err);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    setOffset(0);
    offsetRef.current = 0;
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchBookmarks(false), fetchTags()]);
      setLoading(false);
    };
    loadData();
  }, [fetchBookmarks, fetchTags, isAuthenticated, search, tagFilter]);

  const handleLogin = () => {
    setIsAuthenticated(true);
    fetchBookmarks();
    fetchTags();
  };

  const handleAdd = () => {
    setEditingBookmark(null);
    setModalOpen(true);
  };

  const handleEdit = (bookmark: Bookmark) => {
    setEditingBookmark(bookmark);
    setModalOpen(true);
  };

  const handleView = (bookmark: Bookmark) => {
    setDetailBookmark(bookmark);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this bookmark?")) return;
    
    const prevBookmarks = bookmarks;
    const prevDetailBookmark = detailBookmark;
    
    setBookmarks((prev) => prev.filter((b) => b.id !== id));
    if (detailBookmark?.id === id) {
      setDetailBookmark(null);
    }
    
    try {
      await api.deleteBookmark(id);
      showToast("Bookmark deleted", "success");
    } catch (err) {
      setBookmarks(prevBookmarks);
      setDetailBookmark(prevDetailBookmark);
      showToast(err instanceof Error ? err.message : "Failed to delete", "error");
    }
  };

  const handleSave = async (id: string | null, data: Partial<Bookmark>) => {
    if (id && editingBookmark?.id === id) {
      const { tags, ...updateData } = data;
      const prevBookmarks = bookmarks;
      const prevDetailBookmark = detailBookmark;
      
      const optimisticBookmark = {
        ...editingBookmark,
        ...updateData,
        tags: typeof tags === 'string' ? tags : JSON.stringify(tags || []),
        updatedAt: new Date(),
      };
      
      setBookmarks((prev) =>
        prev.map((b) => (b.id === id ? optimisticBookmark : b))
      );
      if (detailBookmark?.id === id) {
        setDetailBookmark(optimisticBookmark);
      }
      
      try {
        const response = await api.updateBookmark(id, {
          ...updateData,
          tags: tags as any,
        });
        setBookmarks((prev) =>
          prev.map((b) => (b.id === id ? response.data : b))
        );
        if (detailBookmark?.id === id) {
          setDetailBookmark(response.data);
        }
        showToast("Bookmark updated", "success");
      } catch (err) {
        setBookmarks(prevBookmarks);
        setDetailBookmark(prevDetailBookmark);
        showToast(err instanceof Error ? err.message : "Failed to update", "error");
      }
    } else if (!id) {
      const tempId = `temp-${Date.now()}`;
      const prevBookmarks = bookmarks;
      
      const optimisticBookmark: Bookmark = {
        id: tempId,
        url: data.url || "",
        title: data.title || "",
        description: data.description,
        notes: data.notes,
        tags: typeof data.tags === 'string' ? data.tags : JSON.stringify(data.tags || []),
        isArchived: false,
        createdAt: new Date(),
        updatedAt: new Date(),
        isRead: false,
        readLater: false,
      };
      
      setBookmarks((prev) => [optimisticBookmark, ...prev]);
      
      try {
        const response = await api.createBookmark(data as any);
        setBookmarks((prev) =>
          prev.map((b) => (b.id === tempId ? response.data : b))
        );
        showToast("Bookmark added", "success");
      } catch (err) {
        setBookmarks(prevBookmarks);
        showToast(err instanceof Error ? err.message : "Failed to create", "error");
      }
    }
    await fetchTags();
  };

  const handleToggleReadLater = async (bookmark: Bookmark) => {
    const prevBookmarks = bookmarks;
    const prevDetailBookmark = detailBookmark;
    const newReadLaterValue = !bookmark.readLater;
    
    const optimisticBookmark = { ...bookmark, readLater: newReadLaterValue };
    
    setBookmarks((prev) =>
      prev.map((b) => (b.id === bookmark.id ? optimisticBookmark : b))
    );
    if (detailBookmark?.id === bookmark.id) {
      setDetailBookmark(optimisticBookmark);
    }
    showToast(
      newReadLaterValue ? "Added to Read Later" : "Removed from Read Later",
      "success"
    );
    
    try {
      const response = await api.updateBookmark(bookmark.id, {
        readLater: newReadLaterValue,
      });
      setBookmarks((prev) =>
        prev.map((b) => (b.id === bookmark.id ? response.data : b))
      );
      if (detailBookmark?.id === bookmark.id) {
        setDetailBookmark(response.data);
      }
    } catch (err) {
      setBookmarks(prevBookmarks);
      setDetailBookmark(prevDetailBookmark);
      showToast(err instanceof Error ? err.message : "Failed to update", "error");
    }
  };

  const handleArchive = async (bookmark: Bookmark) => {
    if (bookmark.isArchived) {
      setReadingBookmarkId(bookmark.id);
      return;
    }
    
    const prevBookmarks = bookmarks;
    const optimisticBookmark = { ...bookmark, isArchived: true };
    
    setBookmarks((prev) =>
      prev.map((b) => (b.id === bookmark.id ? optimisticBookmark : b))
    );
    showToast("Archiving page...", "info");
    
    try {
      await api.archiveBookmark(bookmark.id);
      showToast("Page archived successfully", "success");
      setReadingBookmarkId(bookmark.id);
    } catch (err) {
      setBookmarks(prevBookmarks);
      showToast(err instanceof Error ? err.message : "Failed to archive", "error");
    }
  };

  const handleCreateTag = async (name: string) => {
    const prevTags = tags;
    const tempId = -Date.now();
    
    const optimisticTag: Tag = {
      id: tempId,
      name,
      createdAt: new Date(),
    };
    
    setTags((prev) => [...prev, optimisticTag]);
    showToast(`Tag "${name}" created`, "success");
    
    try {
      const response = await api.createTag(name);
      setTags((prev) =>
        prev.map((t) => (t.id === tempId ? response.data : t))
      );
    } catch (err) {
      setTags(prevTags);
      showToast(err instanceof Error ? err.message : "Failed to create tag", "error");
    }
  };

  const handleExport = async (format: "json" | "pinboard", filters?: { tag?: string; fromDate?: string; toDate?: string }) => {
    try {
      const response = await api.exportBookmarks({ format, ...filters });
      const blob = new Blob([JSON.stringify(response, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      
      let filename = `pbookmarks-export-${format}-${new Date().toISOString().split('T')[0]}`;
      if (filters?.tag) filename += `-tag-${filters.tag}`;
      if (filters?.fromDate || filters?.toDate) filename += '-filtered';
      filename += '.json';
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      const count = response.bookmarks.length;
      showToast(`Export downloaded successfully (${count} bookmark${count !== 1 ? 's' : ''})`, "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to export", "error");
    }
  };

  const handleImport = async (data: File | string, type: "json" | "html" | "paste") => {
    try {
      let result;
      
      if (type === "html" && data instanceof File) {
        const text = await data.text();
        result = await api.importBookmarksHtml(text);
      } else if (type === "json" && data instanceof File) {
        const text = await data.text();
        const parsed = JSON.parse(text);
        result = await api.importBookmarks(parsed.bookmarks || parsed);
      } else if (type === "paste" && typeof data === "string") {
        const parsed = JSON.parse(data);
        result = await api.importBookmarks(parsed.bookmarks || parsed);
      } else {
        throw new Error("Invalid import data");
      }

      showToast(
        `Import complete: ${result.imported} imported, ${result.skipped} skipped`,
        "success"
      );
      await fetchBookmarks();
      await fetchTags();
      setImportExportModalOpen(false);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to import", "error");
    }
  };

  const filteredBookmarks = bookmarks.filter((b) => {
    if (filter === "read-later") return b.readLater;
    if (filter === "read") return !b.isRead && !b.readLater;
    return true;
  });

  useKeyboardShortcuts({
    bookmarks: filteredBookmarks,
    onSearch: () => document.querySelector<HTMLInputElement>('input[type="text"]')?.focus(),
    onAdd: handleAdd,
    onSelect: handleView,
    isModalOpen: modalOpen || detailBookmark !== null,
  });

  const readLaterCount = bookmarks.filter((b) => b.readLater).length;
  const unreadCount = bookmarks.filter((b) => !b.isRead && !b.readLater).length;

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <LoginModal onLogin={handleLogin} />;
  }

  return (
    <div class="min-h-screen bg-[#fdfcfb] dark:bg-[#1a1614] text-stone-900 dark:text-stone-100 transition-colors selection:bg-primary-100 dark:selection:bg-primary-900/30">
      <Header onAddClick={handleAdd} onImportExportClick={() => setImportExportModalOpen(true)} />

      <main class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div class="mb-10 space-y-8">
          <div class="relative">
            <SearchBar value={search} onChange={setSearch} />
          </div>

          <div class="flex flex-wrap gap-4 items-center justify-between border-b border-stone-200 dark:border-stone-800 pb-2">
            <div class="flex gap-1 items-center">
              <button
                onClick={() => setFilter("all")}
                class={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                  filter === "all"
                    ? "bg-stone-100 dark:bg-stone-800 text-primary-700 dark:text-primary-400 shadow-sm"
                    : "text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-50 dark:hover:bg-stone-800/50"
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter("read-later")}
                class={`px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${
                  filter === "read-later"
                    ? "bg-stone-100 dark:bg-stone-800 text-primary-700 dark:text-primary-400 shadow-sm"
                    : "text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-50 dark:hover:bg-stone-800/50"
                }`}
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Read Later
                {readLaterCount > 0 && (
                  <span class="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400">
                    {readLaterCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setFilter("read")}
                class={`px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${
                  filter === "read"
                    ? "bg-stone-100 dark:bg-stone-800 text-primary-700 dark:text-primary-400 shadow-sm"
                    : "text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-50 dark:hover:bg-stone-800/50"
                }`}
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                Read
                {unreadCount > 0 && (
                  <span class="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-stone-200 dark:bg-stone-700 text-stone-600 dark:text-stone-400">
                    {unreadCount}
                  </span>
                )}
              </button>
            </div>
            
            <div class="flex items-center gap-3">
              <div class="hidden sm:block border-l border-stone-200 dark:border-stone-800 h-6"></div>
              <TagFilter tags={tags} selectedTag={tagFilter} onSelectTag={setTagFilter} />
            </div>
          </div>
        </div>

        <BookmarkList
          bookmarks={filteredBookmarks}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={offset < totalCount}
          search={search}
          onDelete={handleDelete}
          onEdit={handleEdit}
          onView={handleView}
          onToggleReadLater={handleToggleReadLater}
          onArchive={handleArchive}
          onLoadMore={handleLoadMore}
        />
      </main>

      <BookmarkModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        bookmark={editingBookmark}
        availableTags={tags}
        onCreateTag={handleCreateTag}
      />

      <ImportExportModal
        isOpen={importExportModalOpen}
        onClose={() => setImportExportModalOpen(false)}
        onExport={handleExport}
        onImport={handleImport}
        availableTags={tags}
      />

      {detailBookmark && (
        <BookmarkDetail
          bookmark={detailBookmark}
          onClose={() => setDetailBookmark(null)}
          onUpdate={handleSave}
          onDelete={handleDelete}
          onToggleReadLater={handleToggleReadLater}
          onArchive={handleArchive}
        />
      )}

      {readingBookmarkId && (
        <ReadingView
          bookmarkId={readingBookmarkId}
          onClose={() => setReadingBookmarkId(null)}
        />
      )}
    </div>
  );
};

const App: FunctionalComponent = () => {
  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  );
};

export default App;
